/**
 * EffluentEntryPage.tsx
 *
 * Dedicated effluent sample entry workflow with:
 *  • Link to parent influent sample
 *  • Live per-parameter compliance check vs active regulatory standard
 *  • All 30+ parameters (core + physical + metals + nutrients)
 *  • Post-save compliance summary with PDF export
 *  • Discharge gate shortcut
 */

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import {
  Beaker, Save, ArrowRight, Building2, FlaskConical,
  TestTube, Hash, Calendar, CheckCircle2,
  AlertTriangle, RotateCcw, ChevronDown, Shield,
  ShieldCheck, ShieldX, Download, Link2, Droplets,
  Info, Lock, Unlock, TrendingDown, Activity,
  FileText, ChevronRight, X, Globe, MapPin, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useSession } from '@/context/SessionContext';
import { useProposal } from '@/context/ProposalContext';
import { getAllLocalSamples, saveLocalInfluentSample, markSampleDbConfirmed, getLocalSites, saveLocalSite, type UnifiedSample, type CustomerSite } from '@/lib/hybridStorage';
import { hasPhpBackend, apiListCustomerSites, apiSaveInfluentSample } from '@/lib/phpApi';
import { ALL_STANDARDS, type StandardCode, type WastewaterStandard } from '@/types/standards';
import { generateTrendReportPDF, buildRegulatoryReportNumber } from '@/lib/pdfGenerator';
import RegulatoryNotice from '@/components/RegulatoryNotice';
import PageHeader from '@/components/PageHeader';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EffluentForm {
  sampleName: string;
  customerId: string;
  siteId: string;
  linkedSampleId: string;
  standard: string;
  date: string;
  notes: string;
  samplingMethod: string;
  sampledBy: string;
  // Core
  ph: string; cod: string; bod: string; bod5: string; tss: string;
  nh3n: string; tp: string; tn: string; oilgrease: string; tds: string;
  // Physical
  conductivity: string; temperature: string; turbidity: string; flowrate: string;
  // Metals
  lead: string; chromium: string; cadmium: string; nickel: string;
  zinc: string; copper: string; iron: string; manganese: string; arsenic: string;
  mercury: string;
  // Other
  sulfide: string; phenol: string; surfactants: string; chloride: string;
  alkalinity: string; hardness: string; do: string; no3n: string;
}

const EMPTY_FORM: EffluentForm = {
  sampleName: '', customerId: '', siteId: '', linkedSampleId: '', standard: 'UK_EA',
  date: new Date().toISOString().split('T')[0], notes: '',
  samplingMethod: 'composite', sampledBy: '',
  ph: '', cod: '', bod: '', bod5: '', tss: '', nh3n: '', tp: '', tn: '',
  oilgrease: '', tds: '',
  conductivity: '', temperature: '', turbidity: '', flowrate: '',
  lead: '', chromium: '', cadmium: '', nickel: '', zinc: '', copper: '',
  iron: '', manganese: '', arsenic: '', mercury: '',
  sulfide: '', phenol: '', surfactants: '', chloride: '',
  alkalinity: '', hardness: '', do: '', no3n: '',
};

interface Customer {
  id: string; name: string; company: string; standard?: string; country?: string;
}

// ─── Parameter-to-standard-limit mapping ──────────────────────────────────────

type LimitKey = keyof WastewaterStandard['limits'];

interface ParamMeta {
  label: string;
  unit: string;
  limitKey: LimitKey | null;
  isRange?: boolean;  // pH — compare against phMin/phMax
  group: 'core' | 'physical' | 'metal' | 'nutrient' | 'other';
  tip?: string;
}

const PARAM_META: Record<keyof Omit<EffluentForm, 'sampleName'|'customerId'|'linkedSampleId'|'standard'|'date'|'notes'|'samplingMethod'|'sampledBy'>, ParamMeta> = {
  ph:          { label: 'pH',            unit: 'pH units', limitKey: null, isRange: true, group: 'core',     tip: 'Range checked against standard min/max' },
  cod:         { label: 'COD',           unit: 'mg/L',     limitKey: 'cod',             group: 'core',     tip: 'Chemical Oxygen Demand' },
  bod:         { label: 'BOD',           unit: 'mg/L',     limitKey: 'bod5',            group: 'core',     tip: 'Biological Oxygen Demand (5-day)' },
  bod5:        { label: 'BOD₅',         unit: 'mg/L',     limitKey: 'bod5',            group: 'core',     tip: 'BOD₅ — use this OR BOD' },
  tss:         { label: 'TSS',           unit: 'mg/L',     limitKey: 'tss',             group: 'core',     tip: 'Total Suspended Solids' },
  nh3n:        { label: 'NH₃-N',        unit: 'mg/L',     limitKey: 'ammoniaN',        group: 'core',     tip: 'Ammonia Nitrogen' },
  tp:          { label: 'Total P',       unit: 'mg/L',     limitKey: 'totalPhosphorus', group: 'nutrient', tip: 'Total Phosphorus' },
  tn:          { label: 'Total N',       unit: 'mg/L',     limitKey: 'totalNitrogen',   group: 'nutrient', tip: 'Total Nitrogen' },
  oilgrease:   { label: 'Oil & Grease',  unit: 'mg/L',     limitKey: 'oilGrease',       group: 'core',     tip: 'O&G — measured gravimetrically' },
  tds:         { label: 'TDS',           unit: 'mg/L',     limitKey: 'tds',             group: 'physical', tip: 'Total Dissolved Solids' },
  conductivity:{ label: 'Conductivity',  unit: 'µS/cm',    limitKey: 'conductivity',    group: 'physical' },
  temperature: { label: 'Temperature',   unit: '°C',       limitKey: 'temperature',     group: 'physical' },
  turbidity:   { label: 'Turbidity',     unit: 'NTU',      limitKey: null,              group: 'physical' },
  flowrate:    { label: 'Flow Rate',     unit: 'm³/day',   limitKey: null,              group: 'physical' },
  lead:        { label: 'Lead (Pb)',     unit: 'mg/L',     limitKey: 'lead',            group: 'metal' },
  chromium:    { label: 'Chromium (Cr)', unit: 'mg/L',     limitKey: 'chromium',        group: 'metal' },
  cadmium:     { label: 'Cadmium (Cd)', unit: 'mg/L',     limitKey: 'cadmium',         group: 'metal' },
  nickel:      { label: 'Nickel (Ni)',   unit: 'mg/L',     limitKey: 'nickel',          group: 'metal' },
  zinc:        { label: 'Zinc (Zn)',     unit: 'mg/L',     limitKey: 'zinc',            group: 'metal' },
  copper:      { label: 'Copper (Cu)',   unit: 'mg/L',     limitKey: 'copper',          group: 'metal' },
  iron:        { label: 'Iron (Fe)',     unit: 'mg/L',     limitKey: null, group: 'metal', tip: 'Fe — site-specific permit condition (typical consent: ≤10 mg/L). No universal EQS limit applies.' },
  manganese:   { label: 'Manganese',     unit: 'mg/L',     limitKey: null, group: 'metal', tip: 'Mn — site-specific permit condition (typical consent: ≤1 mg/L). No universal EQS limit applies.' },
  arsenic:     { label: 'Arsenic (As)',  unit: 'mg/L',     limitKey: 'arsenic',         group: 'metal' },
  mercury:     { label: 'Mercury (Hg)', unit: 'mg/L',     limitKey: 'mercury',         group: 'metal',  tip: 'Most strictly regulated metal — EQS 0.005 mg/L (UK/EU), 0.001 mg/L (AU)' },
  sulfide:     { label: 'Sulfides',      unit: 'mg/L',     limitKey: 'sulfides',        group: 'other' },
  phenol:      { label: 'Phenol',        unit: 'mg/L',     limitKey: 'phenol',          group: 'other' },
  surfactants: { label: 'Surfactants',   unit: 'mg/L',     limitKey: 'surfactants',     group: 'other' },
  chloride:    { label: 'Chlorides',     unit: 'mg/L',     limitKey: 'chlorides',       group: 'other' },
  alkalinity:  { label: 'Alkalinity',    unit: 'mg/L',     limitKey: null,              group: 'other' },
  hardness:    { label: 'Hardness',      unit: 'mg/L',     limitKey: null,              group: 'other' },
  do:          { label: 'Dissolved O₂', unit: 'mg/L',     limitKey: null,              group: 'other' },
  no3n:        { label: 'NO₃-N',        unit: 'mg/L',     limitKey: null,              group: 'other' },
};

type ParamField = keyof typeof PARAM_META;

// ─── Compliance check ─────────────────────────────────────────────────────────

interface ComplianceResult {
  pass: boolean;
  limit: number | null;
  limitLabel: string;
  pct: number;   // value as % of limit (>100 = fail)
  margin: number; // % headroom remaining (negative = failing)
}

function checkParam(field: ParamField, value: string, std: WastewaterStandard): ComplianceResult | null {
  const v = parseFloat(value);
  if (isNaN(v)) return null;
  const meta = PARAM_META[field];
  const L = std.limits;

  if (meta.isRange) {
    // pH: check against phMin/phMax
    const pass = v >= L.phMin && v <= L.phMax;
    return { pass, limit: L.phMax, limitLabel: `${L.phMin}–${L.phMax}`, pct: 0, margin: 0 };
  }

  if (!meta.limitKey) return null;
  const limit = L[meta.limitKey] as number;
  if (!limit || limit <= 0) return null;

  const pct = Math.round((v / limit) * 100);
  const margin = 100 - pct;
  return { pass: v <= limit, limit, limitLabel: `≤ ${limit}`, pct, margin };
}

// ─── Input step / max helpers ─────────────────────────────────────────────────

/** Return the correct <input> step for a given parameter field */
function getInputStep(field: string): string {
  if (field === 'ph')           return '0.01';
  if (['temperature', 'turbidity', 'do'].includes(field)) return '0.1';
  if (['flowrate', 'flow'].includes(field)) return '0.1';
  if (field === 'conductivity') return '1';
  if (field === 'tds')          return '1';
  if (['lead','mercury','cadmium','arsenic','nickel','chromium',
       'zinc','copper','iron','manganese','phenol','sulfide'].includes(field)) return '0.001';
  return '0.1';
}
function getInputMax(field: string): string | undefined {
  if (field === 'ph')          return '14';
  if (field === 'temperature') return '100';
  return undefined;
}

// ─── Param Input with live compliance indicator ───────────────────────────────

function ParamInput({
  field, form, onUpdate, std, compact = false,
}: {
  field: ParamField;
  form: EffluentForm;
  onUpdate: (f: keyof EffluentForm, v: string) => void;
  std: WastewaterStandard | null;
  compact?: boolean;
}) {
  const meta = PARAM_META[field];
  const value = form[field as keyof EffluentForm] as string;
  const compliance = std && value ? checkParam(field, value, std) : null;

  const borderColor = !compliance ? 'border-slate-800/50 focus:border-rb-brand/40'
    : compliance.pass
      ? compliance.pct >= 80 ? 'border-amber-500/50 focus:border-amber-500/60'
                              : 'border-emerald-500/40 focus:border-emerald-500/50'
      : 'border-red-500/60 focus:border-red-500/70';

  const hasValue = value.trim() !== '';

  return (
    <div className={`space-y-1 ${compact ? '' : ''}`}>
      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between gap-1">
        <span className="flex items-center gap-1">
          {meta.label}
          {meta.limitKey || meta.isRange ? (
            <span className="w-1 h-1 rounded-full bg-rb-brand/60" title="Limit checked" />
          ) : null}
        </span>
        <span className="text-[10px] text-slate-400 font-normal normal-case">{meta.unit}</span>
      </label>

      <div className="relative">
        <input
          type="number"
          step={getInputStep(field)}
          min="0"
          max={getInputMax(field)}
          value={value}
          onChange={(e) => onUpdate(field as keyof EffluentForm, e.target.value)}
          placeholder="—"
          className={`w-full pl-3 py-3 sm:py-2 rounded-lg bg-slate-800/50 border text-base sm:text-sm text-slate-200 placeholder-slate-700 focus:outline-none transition-all font-mono text-right tabular-nums ${hasValue && compliance ? 'pr-14' : 'pr-10'} ${borderColor}`}
        />
        <span className="absolute right-6 top-1/2 -translate-y-1/2 text-[9px] text-slate-600 pointer-events-none select-none leading-none">
          {meta.unit}
        </span>
        {hasValue && compliance && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2">
            {compliance.pass
              ? <CheckCircle2 className={`w-3 h-3 ${compliance.pct >= 80 ? 'text-amber-400' : 'text-emerald-400'}`} />
              : <AlertTriangle className="w-3 h-3 text-red-400" />
            }
          </span>
        )}
      </div>

      {/* Inline limit + bar */}
      {hasValue && compliance && (
        <div className="space-y-0.5">
          <div className="flex items-center justify-between text-[10px]">
            <span className={`font-bold ${compliance.pass ? compliance.pct >= 80 ? 'text-amber-400' : 'text-emerald-400' : 'text-red-400'}`}>
              {compliance.pass
                ? compliance.pct >= 80 ? `${100 - compliance.pct}% headroom` : 'PASS'
                : `EXCEEDS by ${compliance.pct - 100}%`
              }
            </span>
            <span className="text-slate-600 font-mono">{compliance.limitLabel}</span>
          </div>
          <div className="h-0.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                !compliance.pass ? 'bg-red-500'
                : compliance.pct >= 80 ? 'bg-amber-500'
                : 'bg-emerald-500'
              }`}
              style={{ width: `${Math.min(compliance.pct, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* No-limit fields just show value received */}
      {hasValue && !compliance && !std && (
        <div className="text-[10px] text-slate-400">No limit set</div>
      )}
    </div>
  );
}

// ─── Compliance Summary Panel ─────────────────────────────────────────────────

function ComplianceSummaryPanel({
  form, std,
}: {
  form: EffluentForm;
  std: WastewaterStandard | null;
}) {
  const paramFields = Object.keys(PARAM_META) as ParamField[];
  const checked = paramFields
    .filter(f => {
      const v = form[f as keyof EffluentForm] as string;
      return v.trim() !== '' && std && checkParam(f, v, std) !== null;
    })
    .map(f => {
      const v = form[f as keyof EffluentForm] as string;
      const c = checkParam(f, v, std!)!;
      return { field: f, meta: PARAM_META[f], value: parseFloat(v), compliance: c };
    });

  const failing = checked.filter(r => !r.compliance.pass);
  const passing = checked.filter(r => r.compliance.pass);
  const warning = passing.filter(r => r.compliance.pct >= 80);
  const clear   = passing.filter(r => r.compliance.pct < 80);
  const pct = checked.length > 0 ? Math.round(passing.length / checked.length * 100) : null;

  if (checked.length === 0) {
    return (
      <div className="p-4 text-center rb-card">
        <Activity className="w-8 h-8 text-slate-400 mx-auto mb-2" />
        <p className="text-[10px] text-slate-600">Enter parameter values to see live compliance analysis</p>
      </div>
    );
  }

  const allPass = failing.length === 0;
  const bannerColor = allPass ? 'border-emerald-500/30 bg-emerald-500/5'
                   : failing.length <= 2 ? 'border-amber-500/30 bg-amber-500/5'
                   : 'border-red-500/30 bg-red-500/5';

  return (
    <div className="overflow-hidden rb-card">
      {/* Banner */}
      <div className={`px-4 py-3 border-b ${bannerColor} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          {allPass
            ? <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            : <ShieldX className="w-4 h-4 text-red-400 shrink-0" />
          }
          <div>
            <p className={`text-xs font-black ${allPass ? 'text-emerald-400' : failing.length <= 2 ? 'text-amber-400' : 'text-red-400'}`}>
              {allPass ? 'Within discharge limits' : `${failing.length} parameter${failing.length > 1 ? 's' : ''} exceeding limits`}
            </p>
            {pct !== null && (
              <p className="text-[10px] text-slate-500">
                {passing.length}/{checked.length} checked · {pct}% compliant
              </p>
            )}
          </div>
        </div>
        {allPass
          ? <Unlock className="w-4 h-4 text-emerald-400 opacity-60" />
          : <Lock className="w-4 h-4 text-red-400 opacity-60" />
        }
      </div>

      {/* Overall bar */}
      {pct !== null && (
        <div className="px-4 py-2 border-b border-slate-800/60">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${allPass ? 'bg-emerald-500' : failing.length <= 2 ? 'bg-amber-500' : 'bg-red-500'}`}
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </div>
            <span className={`text-[10px] font-black w-8 text-right ${allPass ? 'text-emerald-400' : 'text-amber-400'}`}>{pct}%</span>
          </div>
        </div>
      )}

      {/* Summary counts */}
      <div className="grid grid-cols-3 divide-x divide-slate-800/60">
        {[
          { label: 'FAIL', count: failing.length, color: 'text-red-400', bg: failing.length > 0 ? 'bg-red-500/5' : '' },
          { label: 'WARNING', count: warning.length, color: 'text-amber-400', bg: '' },
          { label: 'PASS', count: clear.length, color: 'text-emerald-400', bg: '' },
        ].map(s => (
          <div key={s.label} className={`text-center py-2 ${s.bg}`}>
            <p className={`text-base font-black ${s.color}`}>{s.count}</p>
            <p className="text-[10px] text-slate-600 uppercase">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Failing parameters */}
      {failing.length > 0 && (
        <div className="border-t border-slate-800/60 px-3 py-2 space-y-1">
          <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-1.5">Exceeding Limits</p>
          {failing.map(({ field, meta, value, compliance }) => (
            <div key={field} className="flex items-center justify-between px-2 py-1 rounded bg-red-500/5 border border-red-500/15">
              <span className="text-[10px] font-bold text-slate-300">{meta.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-red-400 font-black">{value.toFixed(2)}</span>
                <span className="text-[10px] text-slate-600">{meta.unit}</span>
                <span className="text-[10px] text-slate-500">({compliance.limitLabel})</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Warning parameters */}
      {warning.length > 0 && (
        <div className="border-t border-slate-800/60 px-3 py-2 space-y-1">
          <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider mb-1.5">Approaching Limits (≥80%)</p>
          {warning.map(({ field, meta, value, compliance }) => (
            <div key={field} className="flex items-center justify-between px-2 py-1 rounded bg-amber-500/5 border border-amber-500/15">
              <span className="text-[10px] text-slate-300">{meta.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-amber-400">{value.toFixed(2)} / {compliance.limit}</span>
                <span className="text-[10px] text-amber-500">{compliance.pct}%</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Standard reference */}
      {std && (
        <div className="border-t border-slate-800/60 px-3 py-2">
          <p className="text-[10px] text-slate-600 italic">{std.flag} Limits per {std.shortName} — {std.report.legalReference}</p>
        </div>
      )}
    </div>
  );
}

// ─── Post-save Result Modal ───────────────────────────────────────────────────

function SaveResultModal({
  sample, std, customer, onClose,
}: {
  sample: UnifiedSample;
  std: WastewaterStandard | null;
  customer: Customer | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const params = sample.params;
  const paramFields = Object.keys(PARAM_META) as ParamField[];

  const results = paramFields
    .filter(f => params[f] !== undefined && std && checkParam(f, String(params[f]), std) !== null)
    .map(f => ({
      field: f,
      meta: PARAM_META[f],
      value: params[f],
      compliance: checkParam(f, String(params[f]), std!)!,
    }));

  const failing = results.filter(r => !r.compliance.pass);
  const passing = results.filter(r => r.compliance.pass);
  const allPass = failing.length === 0;

  const handleExportPDF = () => {
    generateTrendReportPDF({
      id: String(sample.id),
      reportNumber: sample.reportNumber || buildRegulatoryReportNumber('EFF', sample.standard || 'UK_EA'),
      sampleName: sample.sampleName,
      customerName: customer?.name || 'Unknown',
      customerCompany: customer?.company || 'Unknown',
      standard: sample.standard || 'UK_EA',
      date: new Date(sample.createdAt),
      preparerName: 'Reynolds & Bauhm',
      preparerCompany: 'Reynolds & Bauhm Ltd',
      params,
    });
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-lg overflow-hidden rb-section"
      >
        {/* Header */}
        <div className={`px-5 py-4 border-b ${allPass ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {allPass
                ? <ShieldCheck className="w-6 h-6 text-emerald-400" />
                : <ShieldX className="w-6 h-6 text-red-400" />
              }
              <div>
                <h2 className={`text-sm font-black ${allPass ? 'text-emerald-400' : 'text-red-400'}`}>
                  {allPass ? 'Effluent Sample Saved — Compliant' : 'Effluent Sample Saved — Review Required'}
                </h2>
                <p className="text-[10px] text-slate-500 font-mono mt-0.5">{sample.reportNumber}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/50">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 divide-x divide-slate-800/60 border-b border-slate-800/60">
          {[
            { label: 'Parameters', value: results.length, color: 'text-white' },
            { label: 'Compliant', value: passing.length, color: 'text-emerald-400' },
            { label: 'Failing', value: failing.length, color: failing.length > 0 ? 'text-red-400' : 'text-slate-500' },
          ].map(s => (
            <div key={s.label} className="text-center py-3">
              <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-slate-600 uppercase">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Failing list */}
        {failing.length > 0 && (
          <div className="px-4 py-3 max-h-40 overflow-y-auto border-b border-slate-800/60">
            <p className="text-[10px] font-bold text-red-400 uppercase mb-2">Parameters Requiring Attention</p>
            <div className="space-y-1">
              {failing.map(({ field, meta, value, compliance }) => (
                <div key={field} className="flex items-center justify-between px-2 py-1 rounded bg-red-500/5 border border-red-500/15">
                  <span className="text-[10px] text-slate-300 font-bold">{meta.label}</span>
                  <span className="text-[10px] font-mono">
                    <span className="text-red-400 font-black">{value.toFixed(2)}</span>
                    <span className="text-slate-600"> / {compliance.limit} {meta.unit}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Standard info */}
        {std && (
          <div className="px-4 py-2 border-b border-slate-800/60 bg-slate-800/20">
            <p className="text-[10px] text-slate-500">{std.flag} {std.name} — {std.report.legalReference}</p>
          </div>
        )}

        {/* Actions */}
        <div className="p-4 space-y-2">
          <button
            onClick={handleExportPDF}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-all"
          >
            <Download className="w-3.5 h-3.5" /> Export Compliance PDF
          </button>
          <button
            onClick={() => { navigate('/effluent'); onClose(); }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"
          >
            <Shield className="w-3.5 h-3.5" /> View Discharge Validation Gates
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => { navigate('/customers'); onClose(); }}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold bg-slate-800/60 text-slate-400 border border-slate-700 hover:bg-slate-700 transition-all"
            >
              <Building2 className="w-3 h-3" /> Back to Customers
            </button>
            <button
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold bg-rb-brand/5 text-rb-brand border border-rb-brand/15 hover:bg-rb-brand/10 transition-all"
            >
              <Beaker className="w-3 h-3" /> Add Another Sample
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, label, color, count }: {
  icon: React.ElementType; label: string; color: string; count?: number;
}) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon className={`w-4 h-4 ${color}`} />
      <h3 className={`text-sm font-bold ${color}`}>{label}</h3>
      {count !== undefined && (
        <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 text-[10px] font-bold">{count} params</span>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function EffluentEntryPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { activeStandard, currentCustomer, userProfile } = useSession();
  const userCountry = (userProfile?.country as string | undefined)
    ?? (currentCustomer?.country as string | undefined)
    ?? '';
  const isAuUser = userCountry === 'AU' || userCountry === 'NZ';
  const defaultStandard = isAuUser ? 'AU_EPA' : activeStandard || 'UK_EA';
  const { activeProposal, updateActiveProposal } = useProposal();

  const [form, setForm] = useState<EffluentForm>({
    ...EMPTY_FORM,
    standard: defaultStandard,
    customerId: currentCustomer?.id || '',
    sampleName: `Treated Effluent — ${new Date().toLocaleDateString('en-GB')}`,
  });

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSites, setCustomerSites] = useState<CustomerSite[]>([]);
  const [influents, setInfluents] = useState<UnifiedSample[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedSample, setSavedSample] = useState<UnifiedSample | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showMetals, setShowMetals] = useState(false);
  const [showQuickSite, setShowQuickSite] = useState(false);
  const [quickSiteName, setQuickSiteName] = useState('');
  const [quickSiteAddress, setQuickSiteAddress] = useState('');

  // Report number — uses regulatory numbering format per active standard
  const reportNumber = useMemo(() => {
    const stdCode = ((currentCustomer as any)?.standard || activeStandard || 'UK_EA') as StandardCode;
    return buildRegulatoryReportNumber('EFF', stdCode);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cs = JSON.parse(localStorage.getItem('rb_customers') || '[]');
    setCustomers(cs);
    // Load influent samples
    const all = getAllLocalSamples().filter(s => (s.sampleType || 'influent') === 'influent');
    setInfluents(all);
  }, []);

  useEffect(() => {
    if (!form.customerId) { setCustomerSites([]); return; }
    const numCustId = (() => {
      if (currentCustomer) {
        const sid = (currentCustomer as any).serverId;
        if (sid) return Number(sid);
        const n = Number(currentCustomer.id);
        return currentCustomer.id.length < 13 && n > 0 ? n : 0;
      }
      const n = Number(form.customerId);
      return form.customerId.length < 13 && n > 0 ? n : 0;
    })();

    const localSites = getLocalSites(form.customerId);
    setCustomerSites(localSites);
    setForm(prev => ({ ...prev, siteId: '' }));

    if (isAuthenticated && hasPhpBackend() && numCustId > 0) {
      apiListCustomerSites(numCustId)
        .then(apiSites => {
          if (Array.isArray(apiSites) && apiSites.length > 0) {
            const normSites: CustomerSite[] = apiSites.map((s: any) => ({
              ...s,
              id: String(s.id),
              customerId: String(s.customerId ?? s.customer_id ?? ''),
            }));
            const dbIds = new Set(normSites.map(s => s.id));
            const localOnly = localSites.filter(s => !dbIds.has(s.id));
            setCustomerSites([...normSites, ...localOnly]);
          }
        })
        .catch(() => {});
    }
  }, [form.customerId, isAuthenticated, currentCustomer]);

  // Lock standard to active session customer's standard when set
  useEffect(() => {
    if (currentCustomer && (currentCustomer as any).standard) {
      setForm(prev => ({ ...prev, standard: (currentCustomer as any).standard }));
    } else if (isAuUser) {
      setForm(prev => ({ ...prev, standard: 'AU_EPA' }));
    }
  }, [currentCustomer?.id, isAuUser]);

  // Auto-populate sampledBy from authenticated user
  useEffect(() => {
    if (user) {
      const name = (user as any).name || (user as any).username || (user as any).email || '';
      if (name) setForm(prev => prev.sampledBy ? prev : { ...prev, sampledBy: name });
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve active standard object
  const std = useMemo(() => {
    const code = (form.standard || activeStandard || 'UK_EA') as StandardCode;
    return ALL_STANDARDS.find(s => s.code === code) || null;
  }, [form.standard, activeStandard]);

  // Active customer object
  const activeCustomer = useMemo(
    () => customers.find(c => String(c.id) === String(form.customerId)) || null,
    [customers, form.customerId]
  );

  // Influent samples for selected customer
  const customerInfluents = useMemo(() => {
    if (!form.customerId) return influents;
    return influents.filter(s => String(s.customerId) === String(form.customerId));
  }, [influents, form.customerId]);

  const dafPrediction = useMemo(() => {
    try {
      const ctx = JSON.parse(localStorage.getItem('daf-regulatory-context') || 'null');
      const params = JSON.parse(localStorage.getItem('daf-params-v2') || 'null');
      const size = JSON.parse(localStorage.getItem('daf-size-v2') || 'null');
      if (!ctx || !params || !size) return null;
      return { ctx, params, size };
    } catch { return null; }
  }, []);

  const [dafBannerDismissed, setDafBannerDismissed] = useState(false);

  const applyDafPrediction = () => {
    if (!dafPrediction) return;
    const { params, size } = dafPrediction;
    setForm(f => ({
      ...f,
      bod: params.bod && size.bodRemoval ? String(Math.round(params.bod * (1 - size.bodRemoval))) : f.bod,
      cod: params.cod && size.codRemoval ? String(Math.round(params.cod * (1 - size.codRemoval))) : f.cod,
      tss: params.tss && size.tssRemoval ? String(Math.round(params.tss * (1 - size.tssRemoval))) : f.tss,
      oilgrease: params.og && size.ogRemoval ? String(Math.round(params.og * (1 - size.ogRemoval))) : f.oilgrease,
      ph: params.ph ? String(params.ph.toFixed(2)) : f.ph,
      temperature: params.temp ? String(params.temp) : f.temperature,
    }));
    setDafBannerDismissed(true);
    toast.success('DAF predicted effluent values pre-filled — adjust as needed');
  };

  const update = (field: keyof EffluentForm, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const buildParams = (): Record<string, number> => {
    const p: Record<string, number> = {};
    const fields = Object.keys(PARAM_META) as ParamField[];
    fields.forEach(f => {
      const v = parseFloat(form[f as keyof EffluentForm] as string);
      if (!isNaN(v)) p[f] = v;
    });
    return p;
  };

  const handleSave = async () => {
    if (!form.sampleName.trim()) { toast.error('Sample name is required'); return; }
    if (!form.siteId && customerSites.length > 0) {
      toast.error('Please select a site — effluent samples must be linked to a discharge site for report generation');
      return;
    }
    const params = buildParams();
    if (Object.keys(params).length === 0) { toast.error('Enter at least one parameter value'); return; }

    // Non-compliant save confirmation
    if (std) {
      const failingParams = (Object.keys(PARAM_META) as ParamField[]).filter(f => {
        const v = form[f as keyof EffluentForm] as string;
        if (!v) return false;
        const r = checkParam(f, v, std);
        return r !== null && !r.pass;
      });
      if (failingParams.length > 0) {
        const failList = failingParams.map(f => PARAM_META[f].label).join(', ');
        const confirmed = window.confirm(
          `⚠ Non-Compliant Effluent Sample\n\n` +
          `The following ${failingParams.length} parameter(s) exceed ${std.shortName} discharge limits:\n\n${failList}\n\n` +
          `Saving this sample will create a non-compliant discharge record.\n\n` +
          `Operator: ${form.sampledBy || 'Unknown'}\n\n` +
          `Do you wish to proceed and save this non-compliant sample?`
        );
        if (!confirmed) return;
        toast.warning(`Non-compliant sample saved — ${failingParams.length} parameter(s) exceed limits`);
      }
    }

    setSaving(true);
    // Resolve DB customer ID — prefer serverId to avoid timestamp IDs being passed as DB keys
    const resolvedDbCustId = currentCustomer
      ? ((currentCustomer as any).serverId
          ?? (currentCustomer.id.length < 13 && Number(currentCustomer.id) > 0 ? Number(currentCustomer.id) : null))
      : (form.customerId ? parseInt(form.customerId) : null);
    const cid = resolvedDbCustId;
    const lid = form.linkedSampleId ? parseInt(form.linkedSampleId) : null;

    // Resolve numeric siteId (DB integer IDs are short; local timestamp IDs are 13-digit)
    const numSiteId = form.siteId
      ? (form.siteId.length < 13 && Number(form.siteId) > 0 ? Number(form.siteId) : null)
      : null;

    const saved = saveLocalInfluentSample({
      id: Date.now(),
      customerId: cid,
      siteId: form.siteId || null,
      userId: 0,
      sampleName: form.sampleName.trim(),
      reportNumber,
      params,
      standard: form.standard,
      notes: form.notes || null,
      sampleType: 'effluent',
      linkedSampleId: lid,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    // Sync to DB (effluent = sampleType 'effluent'; deploy/api/influent-samples/save.php handles it)
    if (isAuthenticated && hasPhpBackend() && cid) {
      try {
        const dbResult = await apiSaveInfluentSample({
          customerId: cid,
          siteId: numSiteId,
          sampleName: form.sampleName.trim(),
          reportNumber,
          sampleType: 'effluent',
          linkedSampleId: lid,
          params,
          standard: form.standard,
          notes: form.notes || null,
        });
        // Mark local record as confirmed in DB
        markSampleDbConfirmed(saved.id, (dbResult as any)?.id);
      } catch {
        toast.info('Saved locally — database sync pending');
      }
    }

    // Link to active proposal
    if (activeProposal?.id) {
      updateActiveProposal({
        sampleIds: [...(activeProposal.sampleIds || []), String(saved.id)],
      });
    }

    setSaving(false);
    setSavedSample(saved);
    toast.success(`Effluent sample saved — ${reportNumber}`);
  };

  const handleReset = () => {
    setForm({
      ...EMPTY_FORM,
      standard: defaultStandard,
      customerId: currentCustomer?.id || '',
      sampleName: `Treated Effluent — ${new Date().toLocaleDateString('en-GB')}`,
    });
  };

  // ─── Standard color ─────────────────────────────────────────────────────────
  const stdColor = form.standard === 'AU_EPA' ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10'
                 : form.standard === 'EU_UWWTD' ? 'text-violet-400 border-violet-500/30 bg-violet-500/10'
                 : 'text-blue-400 border-blue-500/30 bg-blue-500/10';

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      <PageHeader
        icon={Beaker}
        iconColor="text-amber-400"
        title="Effluent Sample Entry"
        subtitle="Treated wastewater — regulatory compliance analysis against discharge limits"
        badge={
          <div className="flex items-center gap-2 flex-wrap">
            {std && (
              <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold border ${stdColor}`}>
                <ShieldCheck className="w-3 h-3" />
                {std.flag} {std.shortName}
              </span>
            )}
            <span className="hidden sm:inline-block px-3 py-1.5 rounded-lg bg-slate-800/50 border border-slate-800/50 text-[11px] text-slate-500 font-mono">
              {reportNumber}
            </span>
            {activeProposal && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-violet-500/10 text-violet-400 border border-violet-500/20">
                <FileText className="w-3 h-3" />
                {activeProposal.proposalNumber}
              </span>
            )}
          </div>
        }
      />

      {/* ── Regulatory Origin Banner ── */}
      {!form.customerId ? (
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border border-amber-500/25 bg-amber-500/5">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-amber-400">No customer selected</p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              Select a customer to automatically apply their discharge standard.
            </p>
          </div>
          <button
            onClick={() => navigate('/customers')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all shrink-0"
          >
            Customers <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-rb-brand/20 bg-rb-brand/5">
          <span className="text-base">{std?.flag || '🌍'}</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-rb-brand">Discharge standard applied from customer profile</p>
            <p className="text-[10px] text-slate-500">{std?.name} — {std?.report?.legalReference}</p>
          </div>
          <span className="px-2.5 py-1 rounded-lg text-[10px] font-black bg-rb-brand/10 text-rb-brand border border-rb-brand/20">
            {std?.shortName}
          </span>
        </div>
      )}

      <RegulatoryNotice standard={form?.standard || defaultStandard} context="effluent" compact />

      {/* DAF Prediction Banner */}
      {dafPrediction && !dafBannerDismissed && (
        <div className="mb-4 flex items-start gap-3 px-4 py-3 rounded-xl bg-sky-500/10 border border-sky-500/25 text-sky-300">
          <FlaskConical className="w-4 h-4 mt-0.5 shrink-0 text-sky-400" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-sky-400">DAF Design Available</p>
            <p className="text-[11px] text-sky-500 mt-0.5">
              A DAF calculation exists for <span className="font-semibold text-sky-300">{dafPrediction.ctx?.customerName || 'this customer'}</span>.
              Pre-fill predicted effluent values?
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={applyDafPrediction}
              className="px-3 py-1.5 rounded-lg bg-sky-500/20 border border-sky-500/30 text-sky-300 text-[11px] font-bold hover:bg-sky-500/30 transition-all"
            >
              Pre-fill Values
            </button>
            <button onClick={() => setDafBannerDismissed(true)} className="p-1 text-sky-400 hover:text-sky-400">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_2fr_1fr] gap-6">

        {/* ══ LEFT: Sample Details ══ */}
        <div className="space-y-4">

          {/* Sample Info */}
          <div className="p-4 space-y-3 rb-inner-card">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-800/60">
              <TestTube className="w-4 h-4 text-rb-brand" />
              <h2 className="text-sm font-bold text-slate-200">Sample Details</h2>
            </div>

            {/* Sample Name */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Sample Name *</label>
              <input
                value={form.sampleName}
                onChange={e => update('sampleName', e.target.value)}
                placeholder="e.g. Treated Effluent — Dairy Site"
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-rb-brand/40"
              />
            </div>

            {/* ── Customer — locked when active session is set ── */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Building2 className="w-2.5 h-2.5" /> Customer
              </label>
              {currentCustomer ? (
                <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-cyan-500/8 border border-cyan-500/30">
                  <Lock className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <span className="text-sm font-bold text-cyan-200 flex-1 min-w-0 truncate">
                    {activeCustomer?.company || activeCustomer?.name || currentCustomer.company || currentCustomer.name}
                  </span>
                  {activeCustomer?.standard && (
                    <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 shrink-0">
                      {activeCustomer.standard.replace('_', ' ')}
                    </span>
                  )}
                  <span className="text-[10px] text-cyan-600 shrink-0">session locked</span>
                </div>
              ) : (
                <select
                  value={form.customerId}
                  onChange={e => {
                    const cid = e.target.value;
                    update('customerId', cid);
                    if (cid) {
                      const cust = customers.find(c => String(c.id) === cid);
                      if (cust?.standard && ['UK_EA', 'AU_EPA', 'EU_UWWTD', 'US_EPA'].includes(cust.standard)) {
                        update('standard', cust.standard);
                        toast.info(`Standard auto-set to ${cust.standard.replace(/_/g, ' ')} from customer profile`);
                      }
                    }
                  }}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none"
                >
                  <option value="">— Select customer —</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.company || c.name}</option>
                  ))}
                </select>
              )}
              {form.customerId && activeCustomer?.standard && !currentCustomer && (
                <div className="flex items-center gap-1 px-2 py-1 rounded bg-rb-brand/5 border border-rb-brand/20 text-[10px] text-rb-brand">
                  <Globe className="w-2.5 h-2.5 shrink-0" />
                  Discharge standard derived from customer
                </div>
              )}
            </div>

            {/* ── Discharge Site Location — always selectable, regional laws apply ── */}
            {form.customerId && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    <MapPin className="w-2.5 h-2.5" /> Discharge Site Location
                  </label>
                  {customerSites.length === 0 && (
                    <button
                      type="button"
                      onClick={() => setShowQuickSite(v => !v)}
                      className="flex items-center gap-1 text-[10px] font-bold text-teal-400 hover:text-teal-300 transition-colors"
                    >
                      <Plus className="w-2.5 h-2.5" />
                      {showQuickSite ? 'Cancel' : 'Add site'}
                    </button>
                  )}
                </div>

                {customerSites.length > 0 ? (
                  <>
                    <select
                      value={form.siteId}
                      onChange={e => {
                        const siteId = e.target.value;
                        update('siteId', siteId);
                        if (siteId) {
                          const site = customerSites.find(s => s.id === siteId);
                          const COUNTRY_STD: Record<string, string> = {
                            AU: 'AU_EPA', NZ: 'AU_EPA', GB: 'UK_EA', IE: 'UK_EA',
                            DE: 'EU_UWWTD', FR: 'EU_UWWTD', NL: 'EU_UWWTD', BE: 'EU_UWWTD',
                          };
                          if (site?.standard && ['UK_EA', 'AU_EPA', 'EU_UWWTD'].includes(site.standard)) {
                            update('standard', site.standard);
                            toast.info(`Discharge standard set to ${site.standard.replace(/_/g, ' ')} from site`);
                          } else if (site?.country && COUNTRY_STD[site.country]) {
                            update('standard', COUNTRY_STD[site.country]);
                            toast.info(`${COUNTRY_STD[site.country].replace(/_/g, ' ')} applied — site country ${site.country}`);
                          }
                        }
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none focus:border-teal-500/40"
                    >
                      <option value="">— Select discharge site —</option>
                      {customerSites.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name}{s.state ? ` (${s.state})` : s.region ? ` (${s.region})` : ''}
                        </option>
                      ))}
                    </select>
                    {form.siteId && (() => {
                      const site = customerSites.find(s => s.id === form.siteId);
                      if (!site) return null;
                      return (
                        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-teal-500/5 border border-teal-500/20 text-[10px]">
                          <MapPin className="w-2.5 h-2.5 text-teal-400 shrink-0" />
                          <span className="text-teal-400 font-bold truncate">{site.address}{site.postcode ? `, ${site.postcode}` : ''}</span>
                          {site.permitRef && <span className="ml-auto text-slate-500 shrink-0 font-mono">{site.permitRef}</span>}
                        </div>
                      );
                    })()}
                  </>
                ) : !showQuickSite ? (
                  <p className="text-[10px] text-slate-600 px-2 py-1.5 rounded-lg border border-dashed border-slate-800/50 italic">
                    No discharge sites registered — add one above
                  </p>
                ) : null}

                {showQuickSite && (
                  <div className="space-y-2 p-3 rounded-xl bg-teal-500/5 border border-teal-500/25">
                    <p className="text-[10px] font-bold text-teal-400 uppercase tracking-wider">New Discharge Site</p>
                    <input
                      value={quickSiteName}
                      onChange={e => setQuickSiteName(e.target.value)}
                      placeholder="Site name (e.g. Outfall A, Treatment Works)"
                      className="w-full px-2.5 py-1.5 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-teal-500/40 rb-card-sm"
                    />
                    <input
                      value={quickSiteAddress}
                      onChange={e => setQuickSiteAddress(e.target.value)}
                      placeholder="Address / postcode"
                      className="w-full px-2.5 py-1.5 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-teal-500/40 rb-card-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!quickSiteName.trim()) { toast.error('Site name required'); return; }
                        const newSite = saveLocalSite({
                          customerId: form.customerId,
                          name: quickSiteName.trim(),
                          address: quickSiteAddress.trim() || '',
                          postcode: '', country: activeCustomer?.country || 'GB',
                          standard: activeCustomer?.standard || form.standard,
                          notes: '',
                        });
                        setCustomerSites(getLocalSites(form.customerId));
                        update('siteId', newSite.id);
                        setQuickSiteName(''); setQuickSiteAddress(''); setShowQuickSite(false);
                        toast.success(`Site "${newSite.name}" added`);
                      }}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500/15 border border-teal-500/30 text-[10px] font-bold text-teal-300 hover:bg-teal-500/25 transition-all active:scale-95"
                    >
                      <Plus className="w-3 h-3" /> Save Site
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Link to influent */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Link2 className="w-2.5 h-2.5 text-cyan-400" />
                <span className="text-cyan-400">Link to Influent Sample</span>
                <span className="text-[10px] text-slate-600 font-normal">(optional)</span>
              </label>
              <select
                value={form.linkedSampleId}
                onChange={e => update('linkedSampleId', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-cyan-500/20 text-sm text-slate-300 focus:outline-none focus:border-cyan-500/40"
              >
                <option value="">— No linked sample —</option>
                {customerInfluents.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.sampleName} ({s.reportNumber || `#${s.id}`})
                  </option>
                ))}
              </select>
              {form.linkedSampleId && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-cyan-500/5 border border-cyan-500/15 text-[10px] text-cyan-400">
                  <Link2 className="w-2.5 h-2.5 shrink-0" />
                  Linked to influent #{form.linkedSampleId}
                </div>
              )}
            </div>

            {/* Regulatory Standard */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <ShieldCheck className="w-2.5 h-2.5" /> Regulatory Standard
              </label>
              <select
                value={form.standard}
                onChange={e => update('standard', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!currentCustomer || !!(currentCustomer && (currentCustomer as any).standard)}
              >
                {isAuUser ? (
                  <>
                    <option value="AU_EPA">🇦🇺 Australian EPA (General)</option>
                    <option value="SA_WATER">🇦🇺 SA Water — Trade Waste (SA only)</option>
                  </>
                ) : (
                  <>
                    <option value="UK_EA">🇬🇧 UK Environment Agency</option>
                    <option value="AU_EPA">🇦🇺 Australian EPA</option>
                    <option value="SA_WATER">🇦🇺 SA Water — Trade Waste</option>
                    <option value="EU_UWWTD">🇪🇺 EU Urban Wastewater</option>
                    <option value="US_EPA">🇺🇸 US EPA (NPDES)</option>
                  </>
                )}
              </select>
              {isAuUser && form.standard === 'SA_WATER' && (
                <p className="text-[10px] text-blue-400/80 flex items-center gap-1 mt-0.5">
                  <Info className="w-2.5 h-2.5 shrink-0" /> SA Water trade waste standard selected — excess concentration charges will be calculated
                </p>
              )}
              {!currentCustomer && !isAuUser && (
                <p className="text-[10px] text-amber-400/80 flex items-center gap-1 mt-0.5">
                  <Lock className="w-2.5 h-2.5 shrink-0" /> Set an active customer session to select a regulatory standard
                </p>
              )}
              {currentCustomer && (currentCustomer as any).standard && !isAuUser && (
                <p className="text-[10px] text-sky-400/80 flex items-center gap-1 mt-0.5">
                  <Lock className="w-2.5 h-2.5 shrink-0" /> Standard locked to customer profile: {((currentCustomer as any).standard as string).replace('_', ' ')}
                </p>
              )}
              {std && (
                <div className={`px-2.5 py-1.5 rounded-lg border text-[10px] ${stdColor}`}>
                  <p className="font-bold">{std.name}</p>
                  <p className="opacity-70">{std.permitType}</p>
                </div>
              )}
            </div>

            {/* Date */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Calendar className="w-2.5 h-2.5" /> Sample Date
              </label>
              <input
                type="date"
                value={form.date}
                onChange={e => update('date', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none"
              />
            </div>

            {/* Sampling method */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Sampling Method</label>
              <select
                value={form.samplingMethod}
                onChange={e => update('samplingMethod', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none"
              >
                <option value="composite">Time-composite (recommended)</option>
                <option value="grab">Grab sample</option>
                <option value="flow_proportional">Flow-proportional composite</option>
                <option value="24h_composite">24-hour composite</option>
              </select>
            </div>

            {/* Sampled by */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Sampled By</label>
              <input
                value={form.sampledBy}
                onChange={e => update('sampledBy', e.target.value)}
                placeholder="Name / lab ref"
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-rb-brand/40"
              />
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Notes</label>
              <textarea
                value={form.notes}
                onChange={e => update('notes', e.target.value)}
                rows={2}
                placeholder="Discharge point, permit ref, treatment process…"
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-rb-brand/40 resize-none"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="p-4 space-y-2 rb-inner-card">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-black bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Effluent Sample'}
            </button>
            <button
              onClick={() => { navigate('/effluent'); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"
            >
              <Shield className="w-3.5 h-3.5" /> View Discharge Gates
            </button>
            <button
              onClick={handleReset}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-500 border border-slate-700 hover:bg-slate-700 transition-all"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Clear Form
            </button>
          </div>

          {/* Discharge limit reference */}
          {std && (
            <div className="p-3 space-y-2 rb-card">
              <div className="flex items-center gap-2">
                <Info className="w-3.5 h-3.5 text-slate-500" />
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Key Limits — {std.shortName}</p>
              </div>
              <div className="grid grid-cols-2 gap-1">
                {[
                  { l: 'COD', v: `≤ ${std.limits.cod} mg/L` },
                  { l: 'BOD₅', v: `≤ ${std.limits.bod5} mg/L` },
                  { l: 'TSS', v: `≤ ${std.limits.tss} mg/L` },
                  { l: 'NH₃-N', v: `≤ ${std.limits.ammoniaN} mg/L` },
                  { l: 'O&G', v: `≤ ${std.limits.oilGrease} mg/L` },
                  { l: 'pH', v: `${std.limits.phMin}–${std.limits.phMax}` },
                  { l: 'Total N', v: `≤ ${std.limits.totalNitrogen} mg/L` },
                  { l: 'Total P', v: `≤ ${std.limits.totalPhosphorus} mg/L` },
                  { l: 'Mercury', v: `≤ ${std.limits.mercury} mg/L` },
                  { l: 'Lead', v: `≤ ${std.limits.lead} mg/L` },
                ].map(({ l, v }) => (
                  <div key={l} className="flex items-center justify-between px-2 py-1 rounded bg-slate-800/40">
                    <span className="text-[10px] text-slate-500 font-bold">{l}</span>
                    <span className={`text-[10px] font-mono font-bold ${stdColor.split(' ')[0]}`}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ══ CENTRE: Parameter Entry ══ */}
        <div className="space-y-5">

          {/* ── Core Parameters ── */}
          <div className="p-5 rb-inner-card">
            <SectionHeader icon={FlaskConical} label="Core Parameters" color="text-rb-brand" count={6} />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {(['ph', 'cod', 'bod5', 'tss', 'nh3n', 'oilgrease'] as ParamField[]).map(f => (
                <ParamInput key={f} field={f} form={form} onUpdate={update} std={std} />
              ))}
            </div>
          </div>

          {/* ── Nutrients ── */}
          <div className="p-5 rb-inner-card">
            <SectionHeader icon={TrendingDown} label="Nutrients & Organics" color="text-teal-400" count={4} />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {(['tp', 'tn', 'tds', 'bod'] as ParamField[]).map(f => (
                <ParamInput key={f} field={f} form={form} onUpdate={update} std={std} />
              ))}
            </div>
          </div>

          {/* ── Physical ── */}
          <div className="p-5 rb-inner-card">
            <SectionHeader icon={Activity} label="Physical Parameters" color="text-cyan-400" count={4} />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {(['temperature', 'conductivity', 'turbidity', 'flowrate'] as ParamField[]).map(f => (
                <ParamInput key={f} field={f} form={form} onUpdate={update} std={std} />
              ))}
            </div>
          </div>

          {/* ── Heavy Metals (collapsible) ── */}
          <div className="overflow-hidden rb-inner-card">
            <button
              onClick={() => setShowMetals(v => !v)}
              className="w-full flex items-center justify-between p-5 hover:bg-slate-800/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <TestTube className="w-4 h-4 text-red-400" />
                <span className="text-sm font-bold text-red-400">Heavy Metals</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 text-[10px] font-bold">10 params</span>
                {std && (
                  <span className="text-[10px] text-slate-500">— EQS limits per {std.shortName}</span>
                )}
              </div>
              {showMetals ? <ChevronDown className="w-4 h-4 text-slate-500 rotate-180" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {showMetals && (
              <div className="px-5 pb-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {(['lead', 'chromium', 'cadmium', 'nickel', 'zinc', 'copper', 'iron', 'manganese', 'arsenic', 'mercury'] as ParamField[]).map(f => (
                    <ParamInput key={f} field={f} form={form} onUpdate={update} std={std} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Advanced (collapsible) ── */}
          <div className="overflow-hidden rb-inner-card">
            <button
              onClick={() => setShowAdvanced(v => !v)}
              className="w-full flex items-center justify-between p-5 hover:bg-slate-800/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-bold text-purple-400">Advanced Parameters</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 text-[10px] font-bold">8 params</span>
              </div>
              {showAdvanced ? <ChevronDown className="w-4 h-4 text-slate-500 rotate-180" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {showAdvanced && (
              <div className="px-5 pb-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {(['sulfide', 'phenol', 'surfactants', 'chloride', 'alkalinity', 'hardness', 'do', 'no3n'] as ParamField[]).map(f => (
                    <ParamInput key={f} field={f} form={form} onUpdate={update} std={std} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 text-[10px] text-slate-600 px-1">
            <span className="flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-rb-brand" />= limit checked</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" />= pass</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5 text-amber-400" />= pass ≥80% used</span>
            <span className="flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5 text-red-400" />= exceeds limit</span>
          </div>
        </div>

        {/* ══ RIGHT: Live Compliance ══ */}
        <div className="space-y-4">
          <div className="sticky top-4">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Shield className="w-3 h-3" /> Live Compliance Analysis
            </p>
            <ComplianceSummaryPanel form={form} std={std} />

            {/* Discharge gates status shortcut */}
            <button
              onClick={() => navigate('/effluent')}
              className="mt-4 w-full flex items-center justify-between px-3 py-3 hover:border-emerald-500/30 transition-all group rb-card"
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <div className="text-left">
                  <p className="text-[10px] font-bold text-slate-300">Discharge Validation Gates</p>
                  <p className="text-[10px] text-slate-600">AU / UK regulatory checklist</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 transition-colors" />
            </button>

            {/* Standard compliance notes */}
            {std && (
              <div className="mt-4 p-3 space-y-2 rb-card">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Compliance Requirements</p>
                <div className="space-y-1.5">
                  {std.complianceNotes.slice(0, 4).map((note, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <div className={`w-1 h-1 rounded-full mt-1.5 shrink-0 ${
                        form.standard === 'AU_EPA' ? 'bg-emerald-400'
                        : form.standard === 'EU_UWWTD' ? 'bg-violet-400'
                        : 'bg-blue-400'
                      }`} />
                      <p className="text-[10px] text-slate-500 leading-relaxed">{note}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-600 italic pt-1">{std.actReference}</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── Post-save Modal ── */}
      <AnimatePresence>
        {savedSample && (
          <SaveResultModal
            sample={savedSample}
            std={std}
            customer={activeCustomer}
            onClose={() => setSavedSample(null)}
          />
        )}
      </AnimatePresence>

    </div>
  );
}
